import dayjs from 'dayjs';

// `nextReviewAt` is a date-only value stored as a UTC-midnight ISO timestamp.
// Everything here works off the date part only: parsing the full instant in the
// viewer's local timezone shifts the calendar day in negative-UTC zones (e.g. a
// same-day review flagged overdue, or a date shown one day early). Shared by the
// inline table cell and the slide-over Audit tab so the two can't drift.

// ISO timestamp -> 'YYYY-MM-DD' for a native date input.
export function toDateInputValue(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

// A native date input yields 'YYYY-MM-DD'; store it as UTC midnight so the date
// part round-trips back out via toDateInputValue regardless of timezone.
export function fromDateInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

// Human-readable date, formatted from the date part only (no tz shift).
export function formatReviewDate(iso: string | null): string {
  return iso ? dayjs(iso.slice(0, 10)).format('D MMM YYYY') : '—';
}

// Choosing "Review later" without a date is not a decision to review never, so
// the date defaults to three months out. Returned in date-input form; callers
// wanting a stored value pass it through fromDateInput.
export function defaultNextReviewDateInput(): string {
  return dayjs().add(3, 'month').format('YYYY-MM-DD');
}

// Overdue = the review date is before the viewer's local calendar today.
// Compared as 'YYYY-MM-DD' strings, which sort lexicographically.
export function isReviewOverdue(iso: string | null): boolean {
  return iso !== null && iso.slice(0, 10) < dayjs().format('YYYY-MM-DD');
}

// --- Trust window bounds ---------------------------------------------------
//
// The API caps a trust window at 366 days and rejects a date that is not in the
// future. The picker stops at 365 so a date chosen seconds before midnight
// cannot cross the horizon against a server clock that is marginally ahead.
const MAX_TRUST_DAYS = 365;

// Offsets offered as one-click presets, wherever a review date is picked. 365 is
// the last one because it is also the maximum — an annual review cycle is the
// longest trust window the API allows.
export const REVIEW_PRESET_DAYS = [30, 90, 180, MAX_TRUST_DAYS] as const;

function shiftUtcDays(days: number): string {
  const instant = new Date();
  instant.setUTCDate(instant.getUTCDate() + days);
  return instant.toISOString().slice(0, 10);
}

// A preset offset as a date-input value.
export function presetDateInput(days: number): string {
  return shiftUtcDays(days);
}

/**
 * Whether a reviewer's trust window is still open.
 *
 * `trustedUntil` is a full instant, and the API closes the window at
 * `next_review_at <= now` — so this compares instants, not calendar days, and
 * agrees with the server on the boundary. ISO-8601 UTC strings sort as instants,
 * which is why no parsing is needed.
 */
export function isTrustWindowOpen(trustedUntil: string | null): boolean {
  return trustedUntil !== null && trustedUntil > new Date().toISOString();
}

/**
 * The range a trust date may sit in, as date-input values.
 *
 * Both bounds are computed in UTC because fromDateInput stores the chosen day as
 * UTC midnight and the API compares that instant to now. West of UTC the local
 * calendar tomorrow can already be in the past once converted — late on a UTC-8
 * evening, "tomorrow" is UTC midnight several hours ago — so the lower bound is
 * the later of the local and UTC tomorrows.
 */
export function trustWindowBounds(): { min: string; max: string } {
  const localTomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const utcTomorrow = shiftUtcDays(1);
  return {
    // 'YYYY-MM-DD' sorts lexicographically, so this is a date comparison.
    min: localTomorrow > utcTomorrow ? localTomorrow : utcTomorrow,
    max: shiftUtcDays(MAX_TRUST_DAYS),
  };
}
